<script setup lang="ts">
import { ref, computed } from 'vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'
import { useRouter } from 'vue-router'
import { useIncidentStore } from '@/stores/incident'
import { useAuthStore } from '@/stores/auth'
import { useWizardStore } from '@/stores/wizard'
import type { MissionType } from '@/types'

defineProps<{ visible: boolean }>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  'launched': [incidentId: string]
  'switch-to-wizard': []
}>()

const router = useRouter()
const incidentStore = useIncidentStore()
const authStore = useAuthStore()
const wizardStore = useWizardStore()

const missionName = ref('')
const missionType = ref<MissionType | null>(null)
const startDate = ref(new Date().toLocaleDateString('en-CA'))
const startTime = ref(
  `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`,
)
const launching = ref(false)

const MISSION_TYPES: { value: MissionType; label: string }[] = [
  { value: 'lost_person', label: 'Lost Person' },
  { value: 'rescue', label: 'Rescue' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'other', label: 'Other' },
]

const canLaunch = computed(() => missionName.value.trim() !== '' && !launching.value)

function close() {
  emit('update:visible', false)
}

async function launchMission() {
  if (!canLaunch.value) return
  launching.value = true
  try {
    const { incidentId } = await incidentStore.createIncident({
      incident_name: missionName.value.trim(),
      incident_number: null,
      contact_phone: null,
      agency_id: authStore.userProfile?.agency_id ?? '',
      created_by: authStore.currentUser?.uid ?? '',
      mission_type: missionType.value,
      start_date: startDate.value,
      start_time: startTime.value,
    })
    emit('launched', incidentId)
    close()
    router.push({ name: 'incident-overview', params: { id: incidentId } })
  } finally {
    launching.value = false
  }
}

function switchToWizard() {
  wizardStore.init({
    mission_name: missionName.value.trim() || undefined,
    mission_type: missionType.value ?? undefined,
    start_date: startDate.value,
    start_time: startTime.value,
  })
  close()
  emit('switch-to-wizard')
}
</script>

<template>
  <FosDialog
    :open="visible"
    title="Quick Create"
    max-width="max-w-sm"
    @update:open="emit('update:visible', $event)"
  >
    <div class="flex flex-col gap-5">
      <!-- Mission name -->
      <div class="flex flex-col gap-1.5">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">
          Mission name <span class="text-error">*</span>
        </label>
        <FosInput
          v-model="missionName"
          placeholder="e.g. Lost hiker — Mt. Tamalpais"
        />
      </div>

      <!-- Mission type -->
      <div class="flex flex-col gap-1.5">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">Mission type</label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="mt in MISSION_TYPES"
            :key="mt.value"
            type="button"
            class="px-3 py-1.5 font-mono text-xs uppercase tracking-widest font-bold transition-colors focus:outline-none"
            :class="
              missionType === mt.value
                ? 'bg-primary-container text-white'
                : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'
            "
            @click="missionType = missionType === mt.value ? null : mt.value"
          >
            {{ mt.label }}
          </button>
        </div>
      </div>

      <!-- Date + time -->
      <div class="flex gap-3">
        <div class="flex flex-col gap-1.5 flex-1">
          <label class="font-mono text-xs uppercase tracking-widest text-outline">Start date</label>
          <input
            v-model="startDate"
            type="date"
            class="w-full px-3 py-2 bg-surface-container-high text-on-surface font-mono text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
        </div>
        <div class="flex flex-col gap-1.5 flex-1">
          <label class="font-mono text-xs uppercase tracking-widest text-outline">Start time</label>
          <input
            v-model="startTime"
            type="time"
            class="w-full px-3 py-2 bg-surface-container-high text-on-surface font-mono text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
        </div>
      </div>

      <!-- Hint -->
      <p class="font-mono text-[0.65rem] text-outline">
        You can add subject info, set objectives, and assign comms channels after launch — or
        <button
          type="button"
          class="text-primary hover:text-on-surface underline-offset-2 hover:underline"
          @click="switchToWizard"
        >
          use the setup wizard
        </button>
        to fill everything in now.
      </p>

      <FosButton
        class="w-full"
        :disabled="!canLaunch"
        @click="launchMission"
      >
        {{ launching ? 'Launching...' : 'Launch mission →' }}
      </FosButton>
    </div>
  </FosDialog>
</template>
